import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { HStack } from "@design-system/HStack/HStack";
import { fromIntToMonetary } from "@common/helpers/monetary";
import { currencyFormatter } from "@common/helpers/currency";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { isComplimentaryOrganizationClientPlan } from "@common/constants/organization-billing";
import { useClientPricing } from "@hooks/billing/useClientPricing";
import { ClientDetailsRow } from "../clientViewConfig";
import { EMPTY_CELL_VALUE } from "./lib";
import styles from "./PriceMonthly.module.scss";

export const PriceMonthlyCell = ({
    renderValue,
    row,
}: CellContext<ViewTableFeatures, ClientDetailsRow, FLAT_RATE_PLANS>) => {
    const value = renderValue();
    const { organizationOwnedBillingPricing, basePricing } = useClientPricing();

    if (row.original.isFirmWorkspace || row.original.type === "entity") {
        return EMPTY_CELL_VALUE;
    }

    if (!value) {
        return "-";
    }

    const amountCents =
        organizationOwnedBillingPricing[value] ??
        (isComplimentaryOrganizationClientPlan(value) ? 0 : undefined);

    if (amountCents == null) {
        return "-";
    }

    const formattedAmount = currencyFormatter.format(
        fromIntToMonetary(amountCents),
    );
    const originalAmountCents = basePricing[value] ?? amountCents;

    if (originalAmountCents <= amountCents) {
        return formattedAmount;
    }

    return (
        <HStack gap={4} align="center">
            <s className={styles.originalPrice}>
                {currencyFormatter.format(
                    fromIntToMonetary(originalAmountCents),
                )}
            </s>
            <span>{formattedAmount}</span>
        </HStack>
    );
};
